/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { getAuditLog } from '../utils/api';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

const INVESTOR_STAGES = ['Prospect','Contacted','Intro Call','Deep Dive','Due Diligence','Term Sheet','Closed','Passed','On Hold'];
const PARTNER_STAGES  = ['Prospect','Contacted','Intro Call','Evaluating','Pilot','Active Partner','Churned','On Hold'];
const STATUSES        = ['New','Active','High Priority','Paused','Closed'];
const PRIORITIES      = ['Low','Medium','High'];
const FUND_TYPES      = ['Angel','VC','Family Office','Syndicate','Corporate','Other'];
const CAS_LEVELS      = ['High','Medium','Low'];
const CONTACT_TYPES   = ['Founder','Managing Partner','Partner','Senior Manager','Other'];
const ACTIVITY_OPTIONS = [
  'No Activity',
  'LinkedIn Connection Sent',
  'Connection Accepted',
  'Message Received & Response',
  'Call Scheduled',
  'Meeting Completed',
  'Sent Proposal',
  'Follow Up Sent',
];

const inp = {
  background: 'var(--bg-card)', border: '1px solid var(--border-med)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text)',
  padding: '7px 10px', fontSize: 13,
  fontFamily: 'DM Sans, sans-serif', width: '100%', outline: 'none',
};

const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)', textTransform: 'uppercase',
  letterSpacing: '0.07em', marginBottom: 4,
};

function Field({ label, children }) {
  return <div style={{ marginBottom: 14 }}><label style={lbl}>{label}</label>{children}</div>;
}

function fmtDate(d) {
  if (!d) return '—';
  try { return format(typeof d === 'string' ? parseISO(d) : d, 'MMM d, yyyy h:mm a'); }
  catch { return d; }
}

function HistoryTab({ recordId }) {
  const [log,     setLog]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAuditLog(recordId)
      .then(data => { setLog(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [recordId]);

  if (loading) return (
    <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>Loading history…</div>
  );
  if (!log.length) return (
    <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>
      No history yet. History is recorded on every save.
    </div>
  );

  const sorted = [...log].sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));

  return (
    <div style={{ padding: '0 0 20px' }}>
      {sorted.map((entry, i) => (
        <div key={entry.id} style={{
          padding: '12px 0',
          borderBottom: i < sorted.length - 1 ? '1px solid var(--border)' : 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{fmtDate(entry.changed_at)}</span>
            <span style={{ background: 'var(--primary-dim)', color: 'var(--primary)', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
              {entry.changed_by || 'Unknown'}
            </span>
            {entry.stage_changed && (
              <span style={{ background: 'rgba(167,139,250,0.1)', color: '#A78BFA', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                Stage changed
              </span>
            )}
            {entry.status_changed && (
              <span style={{ background: 'rgba(251,191,36,0.1)', color: '#FBBF24', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                Status changed
              </span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 12 }}>
            {entry.stage     && <div><span style={{ color: 'var(--text-dim)' }}>Stage: </span><span style={{ color: 'var(--text)' }}>{entry.stage}</span></div>}
            <div><span style={{ color: 'var(--text-dim)' }}>Status: </span><span style={{ color: 'var(--text)' }}>{entry.closed ? 'Closed' : 'Open'}</span></div>
            {entry.activity && <div style={{ gridColumn: '1/-1' }}><span style={{ color: 'var(--text-dim)' }}>Activity: </span><span style={{ color: 'var(--text)' }}>{entry.activity}</span></div>}
            <div>
              <span style={{ color: 'var(--text-dim)' }}>Due Date: </span>
              <span style={{ color: 'var(--text)' }}>
                {entry.activity_date
                  ? new Date(entry.activity_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                  : '-'}
              </span>
            </div>
            {entry.notes     && <div style={{ gridColumn: '1/-1' }}><span style={{ color: 'var(--text-dim)' }}>Notes: </span><span style={{ color: 'var(--text-muted)' }}>{entry.notes.substring(0, 120)}{entry.notes.length > 120 ? '…' : ''}</span></div>}
          </div>
        </div>
      ))}
    </div>
  );
}

const DEFAULT_FORM = {
  firm_name: '', contact_name: '', email: '', phone: '', location: '',
  website: '', segment: '', status: 'New', notes: '', activity: 'No Activity',
  activity_date: '', closed: false, stage: '', priority: 'Medium', owner: '',
  use_case: '', uses_qbo: false, check_size: '', fund_type: '', is_lead: false,
  employee_count: '', firm_type: '', managing_partner: '', client_count: '',
  cas_maturity: '', focus: '', fit_for_syncrea: '', linkedin: '', contact_type: '',
};

export default function EditDrawer({ record, segments, activeUser, pipeline, onSave, onDelete, onClose, onSegmentAdded, onOpenFirm }) {
  useBodyScrollLock();
  const [tab,        setTab]        = useState('edit');
  const [form,       setForm]       = useState(DEFAULT_FORM);
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [error,      setError]      = useState('');
  const [newSeg,     setNewSeg]     = useState('');

  const stages     = pipeline === 'Investor' ? INVESTOR_STAGES : PARTNER_STAGES;
  const isInvestor = pipeline === 'Investor';

  useEffect(() => {
    setTab('edit');
    setForm({
      firm_name:        record.firm_name        || '',
      contact_name:     record.contact_name     || '',
      email:            record.email            || '',
      phone:            record.phone            || '',
      location:         record.location         || '',
      website:          record.website          || '',
      segment:          record.segment          || '',
      status:           record.status           || 'New',
      notes:            record.notes            || '',
      activity:         record.activity         || 'No Activity',
      activity_date:    record.activity_date    ? record.activity_date.split('T')[0] : '',
      closed:           false,
      stage:            record.stage            || stages[0],
      priority:         record.priority         || 'Medium',
      owner:            record.owner            || activeUser,
      use_case:         record.use_case         || '',
      uses_qbo:         record.uses_qbo         || false,
      check_size:       record.check_size       || '',
      fund_type:        record.fund_type        || '',
      is_lead:          record.is_lead          || false,
      employee_count:   record.employee_count   || '',
      firm_type:        record.firm_type        || '',
      managing_partner: record.managing_partner || '',
      client_count:     record.client_count     || '',
      cas_maturity:     record.cas_maturity     || '',
      focus:            record.focus            || '',
      fit_for_syncrea:  record.fit_for_syncrea  || '',
      linkedin:         record.linkedin         || '',
      contact_type:     record.contact_type     || '',
    });
    setError('');
  }, [record]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setError('');
    if (!form.firm_name.trim()) {
      setError('Firm Name is required to save.');
      return;
    }
    setSaving(true);
    try {
      await onSave(record.id, {
        ...form,
        firm_name: form.firm_name.trim(),
        type: record.type,
        client_last_updated_at: record.last_updated_at,
      });
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${record.firm_name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try { await onDelete(record.id); }
    finally { setDeleting(false); }
  };

  const addSegment = () => {
    if (!newSeg.trim()) return;
    set('segment', newSeg.trim());
    setNewSeg('');
    onSegmentAdded?.();
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(2px)', zIndex: 200,
      }} />

      <div className="drawer-panel" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 520, maxWidth: '96vw',
        background: 'var(--bg-card2)',
        borderLeft: '1px solid var(--border-med)',
        zIndex: 201, display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
        animation: 'slideIn 0.2s ease',
      }}>
        <style>{`
          @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
          .di:focus { border-color: var(--primary) !important; }
        `}</style>

        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>
              {record.firm_name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {record.location || ''}{record.location ? ' · ' : ''}{record.type}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none',
            color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer',
          }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          {['edit','history'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              flex: 1, background: 'none', border: 'none',
              borderBottom: tab === t ? '2px solid var(--primary)' : '2px solid transparent',
              color: tab === t ? 'var(--primary)' : 'var(--text-muted)',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: tab === t ? 600 : 400,
              fontSize: 13, padding: '10px', cursor: 'pointer',
              marginBottom: -1, textTransform: 'capitalize',
            }}>
              {t === 'edit' ? '✏️ Edit' : '📋 History'}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="drawer-body" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {tab === 'history' ? (
            <HistoryTab recordId={record.id} />
          ) : (
            <>
              {record.source_firm_id && (
                <div className="drawer-banner" style={{ background: 'var(--bg-card)', border: '1px dashed var(--border-med)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', marginBottom: 14, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Converted from Firm Pipeline</span>
                  <button
                    onClick={() => { onOpenFirm?.(record.source_firm_id); onClose?.(); }}
                    style={{
                      background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
                      border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff',
                      fontSize: 11, fontWeight: 600, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    View Firm Record →
                  </button>
                </div>
              )}

              {/* Activity */}
              <div style={{
                background: 'rgba(46,162,235,0.06)',
                border: '1px solid var(--primary-border)',
                borderRadius: 'var(--radius)',
                padding: '14px 16px', marginBottom: 18,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                  ◈ Activity
                </div>
                <Field label="Activity">
                  <select
                    className="di"
                    value={form.activity || 'No Activity'}
                    onChange={e => set('activity', e.target.value)}
                    style={inp}
                  >
                    {ACTIVITY_OPTIONS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Due Date">
                  <input
                    className="di" type="date"
                    value={form.activity_date || ''}
                    onChange={e => set('activity_date', e.target.value)}
                    style={inp}
                  />
                </Field>
                <label className="check-row" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4 }}>
                  <input
                    type="checkbox" checked={form.closed || false}
                    onChange={e => set('closed', e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: 'var(--primary)' }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text)' }}>Closed</span>
                </label>
              </div>

              {/* Stage + Status + Priority + Owner */}
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                <Field label="Stage">
                  <select className="di" value={form.stage || ''} onChange={e => set('stage', e.target.value)} style={inp}>
                    {stages.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select className="di" value={form.status || 'New'} onChange={e => set('status', e.target.value)} style={inp}>
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Priority">
                  <select className="di" value={form.priority || 'Medium'} onChange={e => set('priority', e.target.value)} style={inp}>
                    {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Owner">
                  <select className="di" value={form.owner || ''} onChange={e => set('owner', e.target.value)} style={inp}>
                    <option value="">— none —</option>
                    <option>Bruce</option>
                    <option>Susan</option>
                  </select>
                </Field>
              </div>

              {/* Firm Name */}
              <Field label="Firm Name">
                <input className="di" type="text" value={form.firm_name || ''} onChange={e => set('firm_name', e.target.value)} style={inp} />
              </Field>

              {/* Contact */}
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                <Field label="Primary Contact">
                  <input className="di" type="text" value={form.contact_name || ''} onChange={e => set('contact_name', e.target.value)} style={inp} />
                </Field>
                <Field label="Type of Contact">
                  <select className="di" value={form.contact_type || ''} onChange={e => set('contact_type', e.target.value)} style={inp}>
                    <option value="">— none —</option>
                    {CONTACT_TYPES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                <Field label="Email">
                  <input className="di" type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} style={inp} />
                </Field>
                <Field label="Phone">
                  <input className="di" type="tel" value={form.phone || ''} onChange={e => set('phone', e.target.value)} style={inp} />
                </Field>
              </div>
              <Field label="Location">
                <input className="di" type="text" placeholder="City, State" value={form.location || ''} onChange={e => set('location', e.target.value)} style={inp} />
              </Field>

              {/* Investor-specific */}
              {isInvestor && (
                <>
                  <Field label="LinkedIn">
                    <input className="di" type="text" placeholder="https://linkedin.com/in/…" value={form.linkedin || ''} onChange={e => set('linkedin', e.target.value)} style={inp} />
                  </Field>
                  <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                    <Field label="Type">
                      <select className="di" value={form.fund_type || ''} onChange={e => set('fund_type', e.target.value)} style={inp}>
                        <option value="">— none —</option>
                        {FUND_TYPES.map(f => <option key={f}>{f}</option>)}
                      </select>
                    </Field>
                    <Field label="Typical Check Size ($)">
                      <input className="di" type="number" value={form.check_size || ''} onChange={e => set('check_size', e.target.value)} style={inp} />
                    </Field>
                  </div>
                  <Field label="Focus">
                    <input className="di" type="text" placeholder="e.g. B2B SaaS, Fintech, Early Stage…" value={form.focus || ''} onChange={e => set('focus', e.target.value)} style={inp} />
                  </Field>
                  <Field label="Fit for Syncrea">
                    <textarea className="di" value={form.fit_for_syncrea || ''} onChange={e => set('fit_for_syncrea', e.target.value)} rows={3} placeholder="Why this investor is or isn't a good fit…" style={{ ...inp, resize: 'vertical' }} />
                  </Field>
                  <Field label="Lead Investor">
                    <label className="check-row" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox" checked={form.is_lead || false}
                        onChange={e => set('is_lead', e.target.checked)}
                        style={{ width: 15, height: 15, accentColor: 'var(--primary)' }}
                      />
                      <span style={{ fontSize: 13, color: 'var(--text)' }}>This is a lead investor</span>
                    </label>
                  </Field>
                </>
              )}

              {/* Design Partner-specific */}
              {!isInvestor && (
                <>
                  <Field label="Website">
                    <input className="di" type="text" value={form.website || ''} onChange={e => set('website', e.target.value)} style={inp} />
                  </Field>
                  <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                    <Field label="Firm Type">
                      <input className="di" type="text" value={form.firm_type || ''} onChange={e => set('firm_type', e.target.value)} style={inp} />
                    </Field>
                    <Field label="CAS Maturity">
                      <select className="di" value={form.cas_maturity || ''} onChange={e => set('cas_maturity', e.target.value)} style={inp}>
                        <option value="">— none —</option>
                        {CAS_LEVELS.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </Field>
                    <Field label="# Employees">
                      <input className="di" type="text" value={form.employee_count || ''} onChange={e => set('employee_count', e.target.value)} style={inp} />
                    </Field>
                    <Field label="Est. Clients">
                      <input className="di" type="text" value={form.client_count || ''} onChange={e => set('client_count', e.target.value)} style={inp} />
                    </Field>
                  </div>
                  <Field label="Managing Partner">
                    <input className="di" type="text" value={form.managing_partner || ''} onChange={e => set('managing_partner', e.target.value)} style={inp} />
                  </Field>
                  <Field label="Uses QBO">
                    <label className="check-row" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox" checked={form.uses_qbo || false}
                        onChange={e => set('uses_qbo', e.target.checked)}
                        style={{ width: 15, height: 15, accentColor: 'var(--primary)' }}
                      />
                      <span style={{ fontSize: 13, color: 'var(--text)' }}>Uses QuickBooks Online</span>
                    </label>
                  </Field>
                  <Field label="Use Case">
                    <textarea className="di" value={form.use_case || ''} onChange={e => set('use_case', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} />
                  </Field>
                </>
              )}

              {/* Segment */}
              <Field label="Segment">
                <div style={{ display: 'flex', gap: 6 }}>
                  <select className="di" value={form.segment || ''} onChange={e => set('segment', e.target.value)} style={{ ...inp, flex: 1 }}>
                    <option value="">— none —</option>
                    {segments.map(s => <option key={s}>{s}</option>)}
                  </select>
                  <input
                    type="text" placeholder="New…" value={newSeg}
                    onChange={e => setNewSeg(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addSegment()}
                    style={{ ...inp, width: 80 }}
                  />
                  <button onClick={addSegment} style={{
                    background: 'var(--bg-card)', border: '1px solid var(--border-med)',
                    borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
                    fontSize: 12, padding: '0 10px', cursor: 'pointer',
                  }}>+</button>
                </div>
              </Field>

              {/* Notes / Summary */}
              <Field label={isInvestor ? 'Summary' : 'Notes'}>
                <textarea
                  className="di" value={form.notes || ''}
                  onChange={e => set('notes', e.target.value)}
                  rows={4} style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }}
                />
              </Field>

              <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.8, marginTop: 8 }}>
                <div>Created: {record.created_at ? new Date(record.created_at).toLocaleString() : '—'} by {record.created_by || '—'}</div>
                <div>Last updated: {record.last_updated_at ? new Date(record.last_updated_at).toLocaleString() : '—'} by {record.last_updated_by || '—'}</div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {tab === 'edit' && (
          <div className="drawer-footer" style={{
            padding: '14px 20px', borderTop: '1px solid var(--border)',
            flexShrink: 0, background: 'var(--bg-card)',
          }}>
            {error && (
              <div style={{
                background: 'var(--danger-bg)',
                border: '1px solid rgba(248,113,113,0.3)',
                borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
                fontSize: 12, padding: '7px 12px', marginBottom: 10,
              }}>
                ⚠ {error}
              </div>
            )}
            <div className="drawer-actions" style={{ display: 'flex', gap: 8 }}>
              <button className="drawer-save" onClick={handleSave} disabled={saving} style={{
                flex: 1,
                background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
                border: 'none', borderRadius: 'var(--radius-sm)',
                color: '#fff', fontFamily: 'DM Sans, sans-serif',
                fontWeight: 600, fontSize: 13, padding: '9px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}>
                {saving ? 'Saving…' : '✓ Save'}
              </button>
              <button className="drawer-delete" onClick={handleDelete} disabled={deleting} style={{
                background: 'var(--danger-bg)',
                border: '1px solid rgba(248,113,113,0.2)',
                borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
                fontSize: 13, padding: '9px 16px', cursor: 'pointer',
              }}>
                {deleting ? '…' : 'Delete'}
              </button>
              <button className="drawer-cancel" onClick={onClose} style={{
                background: 'var(--bg-card2)',
                border: '1px solid var(--border-med)',
                borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
                fontFamily: 'DM Sans, sans-serif', fontSize: 13,
                padding: '9px 14px', cursor: 'pointer',
              }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
